import { REVIEW_AI_ERROR_CODES } from '@mrw/review-ai-contracts/contracts';
import { ApiError, extractApiErrorDescriptor, extractApiErrorMessageFromUnknown } from '@/shared/errors/api-error';

export const OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_CODE = 'onboarding.policy.offer_not_accepted';
export const OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_MESSAGE = 'Offer has not been accepted';
export const OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_REASON = 'offer_not_accepted';
export const AI_REQUEST_REJECTED_MARKETPLACE_ERROR_CODE = REVIEW_AI_ERROR_CODES.aiRequestRejected;
export const AI_REQUEST_REJECTED_MARKETPLACE_ERROR_MESSAGE =
    '[server.ai-analysis.generate] AI API request was rejected.';

export const MARKETPLACE_API_ERROR_MESSAGES = {
    ru: {
        offerNotAccepted: 'Сначала примите оферту.',
        aiRequestRejected: 'Сервис ИИ временно не принял запрос. Попробуйте повторить позже.',
    },
    en: {
        offerNotAccepted: 'Accept the offer first.',
        aiRequestRejected: 'The AI service could not accept the request. Try again later.',
    },
    uz: {
        offerNotAccepted: 'Avval ofertani qabul qiling.',
        aiRequestRejected: 'AI xizmati so‘rovni vaqtincha qabul qilmadi. Keyinroq qayta urinib ko‘ring.',
    },
    'uz-Cyrl': {
        offerNotAccepted: 'Аввал офертани қабул қилинг.',
        aiRequestRejected: 'AI хизмати сўровни вақтинча қабул қилмади. Кейинроқ қайта уриниб кўринг.',
    },
} as const;

export type MarketplaceApiErrorLocale = keyof typeof MARKETPLACE_API_ERROR_MESSAGES;
type MarketplaceApiErrorMessageKey = keyof (typeof MARKETPLACE_API_ERROR_MESSAGES)['ru'];

const DEFAULT_MARKETPLACE_API_ERROR_LOCALE: MarketplaceApiErrorLocale = 'ru';

const MARKETPLACE_API_ERROR_LOCALES = Object.keys(MARKETPLACE_API_ERROR_MESSAGES) as MarketplaceApiErrorLocale[];

const selectMarketplaceApiErrorMessages = (messageKey: MarketplaceApiErrorMessageKey) =>
    MARKETPLACE_API_ERROR_LOCALES.reduce(
        (messages, locale) => ({
            ...messages,
            [locale]: MARKETPLACE_API_ERROR_MESSAGES[locale][messageKey],
        }),
        {} as Record<MarketplaceApiErrorLocale, string>,
    );

export const OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_MESSAGES = selectMarketplaceApiErrorMessages('offerNotAccepted');

export const AI_REQUEST_REJECTED_MARKETPLACE_ERROR_MESSAGES = selectMarketplaceApiErrorMessages('aiRequestRejected');

type MarketplaceApiErrorDescriptor = {
    code?: unknown;
    errorCode?: unknown;
    reason?: unknown;
    errorReason?: unknown;
    message?: unknown;
    errorMessage?: unknown;
};

const readOptionalString = (value: unknown): string | null => (typeof value === 'string' ? value.trim() || null : null);

const KNOWN_ERROR_CODE_TO_MESSAGE_KEY = {
    [OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_CODE]: 'offerNotAccepted',
    [AI_REQUEST_REJECTED_MARKETPLACE_ERROR_CODE]: 'aiRequestRejected',
} as const satisfies Record<string, MarketplaceApiErrorMessageKey>;

const KNOWN_ERROR_REASON_TO_MESSAGE_KEY = {
    [OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_REASON]: 'offerNotAccepted',
} as const satisfies Record<string, MarketplaceApiErrorMessageKey>;

const KNOWN_ERROR_MESSAGE_TO_MESSAGE_KEY = {
    [OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_MESSAGE]: 'offerNotAccepted',
    [AI_REQUEST_REJECTED_MARKETPLACE_ERROR_MESSAGE]: 'aiRequestRejected',
    '[server.ai-analysis.generate] AI API request was rejected': 'aiRequestRejected',
    '[server.ai-analysis] AI API request was rejected': 'aiRequestRejected',
    '[server.ai-analysis] AI API request was rejected.': 'aiRequestRejected',
} as const satisfies Record<string, MarketplaceApiErrorMessageKey>;

const readKnownMessageKey = (
    registry: Readonly<Record<string, MarketplaceApiErrorMessageKey>>,
    value: unknown,
): MarketplaceApiErrorMessageKey | null => {
    const normalizedValue = readOptionalString(value);
    return normalizedValue ? (registry[normalizedValue] ?? null) : null;
};

export const resolveKnownMarketplaceApiErrorMessageKey = (
    descriptor: MarketplaceApiErrorDescriptor,
): MarketplaceApiErrorMessageKey | null =>
    readKnownMessageKey(KNOWN_ERROR_CODE_TO_MESSAGE_KEY, descriptor.code) ??
    readKnownMessageKey(KNOWN_ERROR_CODE_TO_MESSAGE_KEY, descriptor.errorCode) ??
    readKnownMessageKey(KNOWN_ERROR_REASON_TO_MESSAGE_KEY, descriptor.reason) ??
    readKnownMessageKey(KNOWN_ERROR_REASON_TO_MESSAGE_KEY, descriptor.errorReason) ??
    readKnownMessageKey(KNOWN_ERROR_MESSAGE_TO_MESSAGE_KEY, descriptor.message) ??
    readKnownMessageKey(KNOWN_ERROR_MESSAGE_TO_MESSAGE_KEY, descriptor.errorMessage);

export const formatKnownMarketplaceApiErrorMessageFromDescriptor = (
    descriptor: MarketplaceApiErrorDescriptor,
    locale: MarketplaceApiErrorLocale = DEFAULT_MARKETPLACE_API_ERROR_LOCALE,
): string | null => {
    const messageKey = resolveKnownMarketplaceApiErrorMessageKey(descriptor);
    return messageKey ? MARKETPLACE_API_ERROR_MESSAGES[locale][messageKey] : null;
};

export const isOfferNotAcceptedMarketplaceErrorDescriptor = (descriptor: MarketplaceApiErrorDescriptor): boolean =>
    resolveKnownMarketplaceApiErrorMessageKey(descriptor) === 'offerNotAccepted';

export const isAiRequestRejectedMarketplaceErrorDescriptor = (descriptor: MarketplaceApiErrorDescriptor): boolean =>
    resolveKnownMarketplaceApiErrorMessageKey(descriptor) === 'aiRequestRejected';

export const resolveOfferNotAcceptedMarketplaceErrorMessage = (
    locale: MarketplaceApiErrorLocale = DEFAULT_MARKETPLACE_API_ERROR_LOCALE,
): string => OFFER_NOT_ACCEPTED_MARKETPLACE_ERROR_MESSAGES[locale];

export const resolveAiRequestRejectedMarketplaceErrorMessage = (
    locale: MarketplaceApiErrorLocale = DEFAULT_MARKETPLACE_API_ERROR_LOCALE,
): string => AI_REQUEST_REJECTED_MARKETPLACE_ERROR_MESSAGES[locale];

const isRecord = (value: unknown): value is Record<string, unknown> =>
    Object.prototype.toString.call(value) === '[object Object]';

const collectPotentialMarketplaceApiErrorDescriptors = (error: unknown): MarketplaceApiErrorDescriptor[] => {
    const descriptors: MarketplaceApiErrorDescriptor[] = [];

    if (error instanceof ApiError) {
        descriptors.push({
            code: error.code,
            message: error.message,
        });
    }

    if (isRecord(error)) {
        const responseData = isRecord(error.response) ? error.response.data : undefined;
        if (responseData !== undefined) {
            const responseDescriptor = extractApiErrorDescriptor(responseData);
            if (responseDescriptor) {
                descriptors.push(responseDescriptor);
            }

            if (isRecord(responseData)) {
                descriptors.push(responseData);
                if (isRecord(responseData.error)) {
                    descriptors.push(responseData.error);
                }
            }
        }

        const extractedDescriptor = extractApiErrorDescriptor(error);
        if (extractedDescriptor) {
            descriptors.push(extractedDescriptor);
        }
        descriptors.push(error);
    }

    return descriptors;
};

export const formatMarketplaceApiErrorMessageFromUnknown = (
    error: unknown,
    options: {
        locale?: MarketplaceApiErrorLocale;
        fallbackMessage?: string;
    } = {},
): string | null => {
    const locale = options.locale ?? DEFAULT_MARKETPLACE_API_ERROR_LOCALE;

    for (const descriptor of collectPotentialMarketplaceApiErrorDescriptors(error)) {
        const knownMessage = formatKnownMarketplaceApiErrorMessageFromDescriptor(descriptor, locale);
        if (knownMessage) {
            return knownMessage;
        }
    }

    return extractApiErrorMessageFromUnknown(error) ?? options.fallbackMessage ?? null;
};
